import { Box, Heading, TextButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { ArrowRight } from '@wix/wix-ui-icons-common/lazy'
import React, { FC } from 'react'
import { useSearchParams } from 'react-router-dom'

import { markAsWixStudioAccount } from '../../../api/StudioWorkspaceService/markAsWixStudioAccount'
import { unmarkAsWixStudioAccount } from '../../../api/StudioWorkspaceService/unmarkAsWixStudioAccount'
import { usePageLogger } from '../../../bi/mfi'
import { RichText } from '../../reusable/RichText'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import s from '../WixStudioCreate/WixStudioCreate.scss'
import { Option } from './Option/Option'
import type { TWixStudioSplit as Content } from './WixStudioSplit.content'
import { wixStudioSplitDataHooks as dataHooks } from './WixStudioSplit.dataHooks'

export const WixStudioSplit: FC = () => {
  const [searchParams] = useSearchParams()
  const [isPageLoading] = usePageLoading()
  const next = useNext()
  const { content } = useContent<Content>()
  const { question, secondaryQuestion } = content
  const pageLogger = usePageLogger()

  const authMethod = searchParams.get('authMethod')
  const isDisclaimerVisible = content.disclaimer && authMethod === 'GOOGLE_ONE_TAP'

  const handleClick =
    (questionKey: string, optionKey: string, markAsStudio: boolean, unmarkAsStudio: boolean) => () => {
      if (isPageLoading) {
        return
      }
      if (markAsStudio) {
        void markAsWixStudioAccount()
      } else if (unmarkAsStudio) {
        void unmarkAsWixStudioAccount()
      }

      void pageLogger.answer({ answer: optionKey })
      next({
        [questionKey]: { values: [optionKey] },
      })
    }

  const secondaryOption = secondaryQuestion?.options[0]

  return (
    <WixStudioTemplate
      backButton={content.back}
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      sidePanel={null}
      bottomNote={
        isDisclaimerVisible && (
          <Box maxWidth="580px" textAlign="center" margin="0 auto">
            <RichText light secondary size="small" className={s.disclaimer} dataHook={dataHooks.disclaimer()}>
              {content.disclaimer}
            </RichText>
          </Box>
        )
      }
    >
      <Box direction="vertical" paddingBottom="SP2">
        <Box align="center" direction="vertical" gap="SP2">
          <Heading dataHook={dataHooks.title()}>{question.content.title}</Heading>
          {Boolean(question.content.subtitle) && (
            <Heading className={s.WixStudioSplit__subtitle}>{question.content.subtitle}</Heading>
          )}
        </Box>
        <Box gap="SP3" align="center" marginTop="SP5">
          {question.options.map(option => (
            <Option
              key={option.optionKey}
              imgSrc={option.img.content.src}
              label={option.label}
              btn={option.btn}
              description={option.description}
              onClick={handleClick(question.questionKey, option.optionKey, option.markAsStudio, option.unmarkAsStudio)}
              dataHook={dataHooks.option({ key: option.optionKey })}
            />
          ))}
        </Box>
        {secondaryOption && (
          <Box marginTop="SP10" align="center">
            <TextButton
              skin="standard"
              dataHook={dataHooks.secondaryOption({ key: secondaryOption.optionKey })}
              className={s.WixStudioCreate__defaultLinkButton}
              suffixIcon={<ArrowRight />}
              onClick={handleClick(
                secondaryQuestion.questionKey,
                secondaryOption.optionKey,
                secondaryOption.markAsStudio,
                secondaryOption.unmarkAsStudio,
              )}
            >
              {secondaryOption.label}
            </TextButton>
          </Box>
        )}
      </Box>
    </WixStudioTemplate>
  )
}
